import { LngLat, LngLatBounds, type FitBoundsOptions, type LngLatBoundsLike, type LngLatLike } from 'maplibre-gl';

import type { MapInstance } from './types.ts';

const FIT_ITERATIONS = 8;
const UPWARD_OFFSET_RATIO = 0.05;

/** Frames projected geometry without moving the map or zooming out beyond the original fit. */
export const fitPitchedBounds = (
  map: MapInstance,
  bounds: LngLatBoundsLike,
  options: FitBoundsOptions,
  coordinates?: LngLatLike[],
) => {
  const camera = map.cameraForBounds(bounds, options);
  if (!camera?.center || camera.zoom === undefined) {
    return camera;
  }

  // Keep MapLibre's internal transform access here; all adjustments operate on a copy.
  const transform = map._camera.transform.clone();
  transform.setCenter(LngLat.convert(camera.center));
  transform.setZoom(camera.zoom);
  transform.setBearing(options.bearing ?? map.getBearing());
  transform.setPitch(options.pitch ?? map.getPitch());
  const padding = options.padding;
  const {
    top = 0,
    bottom = 0,
    left = 0,
    right = 0,
  } = typeof padding === 'number' ? { top: padding, bottom: padding, left: padding, right: padding } : (padding ?? {});
  const availableWidth =
    transform.width - (transform.padding.left ?? 0) - (transform.padding.right ?? 0) - left - right;
  const availableHeight =
    transform.height - (transform.padding.top ?? 0) - (transform.padding.bottom ?? 0) - top - bottom;
  if (availableWidth <= 0 || availableHeight <= 0) {
    return camera;
  }
  const upwardOffset = availableHeight * UPWARD_OFFSET_RATIO;
  const target = transform.centerPoint.clone();
  target.x += (left - right) / 2;
  target.y += (top - bottom) / 2 - upwardOffset;
  const box = LngLatBounds.convert(bounds).adjustAntiMeridian();
  const locations = coordinates?.length
    ? coordinates.map(coordinate => LngLat.convert(coordinate))
    : [box.getNorthWest(), box.getNorthEast(), box.getSouthEast(), box.getSouthWest()];
  const measure = () => {
    const points = locations.map(location => transform.locationToScreenPoint(location));
    const xs = points.map(point => point.x);
    const ys = points.map(point => point.y);
    const min = points[0].clone();
    const max = points[0].clone();
    min.x = Math.min(...xs);
    min.y = Math.min(...ys);
    max.x = Math.max(...xs);
    max.y = Math.max(...ys);
    return { center: min.add(max).div(2), width: max.x - min.x, height: max.y - min.y };
  };

  for (let i = 0; i < FIT_ITERATIONS; i++) {
    const { center } = measure();
    transform.setLocationAtPoint(transform.screenPointToLocation(center), target);
    if (i === FIT_ITERATIONS - 1) {
      break;
    }
    const { width, height } = measure();
    // Keep the top clear while reserving a little more space below the route.
    const scale = Math.min(
      availableWidth / Math.max(width, 1),
      (availableHeight - 2 * upwardOffset) / Math.max(height, 1),
    );
    transform.setZoom(
      Math.max(camera.zoom, Math.min(options.maxZoom ?? transform.maxZoom, transform.zoom + Math.log2(scale))),
    );
  }
  return { ...camera, center: transform.center, zoom: transform.zoom };
};
